/**
 * Type-safe project authorization hook
 *
 * Wraps useResourceAuth with typed project actions for compile-time safety.
 *
 * @example
 * ```typescript
 * import { useProjectAuth } from "@/hooks/useProjectAuth";
 * import { PROJECT_ACTIONS } from "@/constants/auth";
 *
 * function ProjectSettings({ project }) {
 *   const { can } = useProjectAuth(project.auth);
 *
 *   return (
 *     <>
 *       {can(PROJECT_ACTIONS.UPDATE) && <EditButton />}
 *       {can(PROJECT_ACTIONS.DELETE) && <DeleteButton />}
 *     </>
 *   );
 * }
 * ```
 */

import { useResourceAuth } from "./useResourceAuth";
import type { AuthPermission } from "@/types";
import type { ProjectAction } from "@/constants/auth";

export function useProjectAuth(auth?: Record<string, AuthPermission>) {
  const { can: canAny, getPermission } = useResourceAuth(auth);

  const can = (action: ProjectAction): boolean => canAny(action);

  return { can, getPermission };
}
